import {
  Grid,
  Box,
  TextField,
  Button,
  Switch,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import Axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { Loading } from "../../components/shared/loading";
import { useAdminStyles } from "../../components/admin/adminStyles";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { useToast } from "../../components/shared/Toast";

import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import InsertPhotoOutlinedIcon from "@material-ui/icons/InsertPhotoOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SaveIcon from "@material-ui/icons/Save";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";
const PRIORITY_OPTIONS = [1, 2, 3, 4, 5, 6];

export const UpdateTeam = () => {
  const classes = useAdminStyles();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [skills, setSkills] = useState("");
  const [disgn, setDisgn] = useState("");
  const [desc, setDesc] = useState("");
  const [empPic, setEmpPic] = useState({ raw: "", preview: "" });
  const [status, setStatus] = useState(true);
  const [priority, setPriority] = useState("");
  const [oldImage, setOldImage] = useState("");
  const [err, setErr] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const formData = new FormData();
    formData.append("idd", id);
    Axios.post("/api/select_single_team", formData, {
      headers: { headKey: headKey },
    }).then((res) => {
      if (res.data.status) {
        const d = res.data.data;
        setName(d.name || "");
        setPriority(String(d.priority || ""));
        setSkills(d.skill || "");
        setDisgn(d.designation || "");
        setDesc(d.description || "");
        if (d.image) setOldImage(d.image);
        setStatus(d.status === 1);
        setIsLoading(false);
      } else {
        toast.error("Could not load team member");
        navigate("/dash/team");
      }
    });
  }, [id, navigate, toast]);

  const handleImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Please select only JPEG, JPG, PNG or WebP images.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5 MB.");
      return;
    }
    setEmpPic({
      preview: URL.createObjectURL(file),
      raw: file,
    });
  };

  const onSubmit = () => {
    setErr(0);
    if (!name.trim()) {
      setErr(1);
      toast.error("Please enter the member's name.");
      return;
    }
    if (!disgn.trim()) {
      setErr(2);
      toast.error("Please enter the designation.");
      return;
    }
    if (!skills.trim()) {
      setErr(3);
      toast.error("Please enter the skills.");
      return;
    }
    if (!desc.trim()) {
      setErr(4);
      toast.error("Please enter a description.");
      return;
    }
    if (!priority) {
      setErr(6);
      toast.error("Please select a priority.");
      return;
    }
    if (!empPic.raw && !oldImage) {
      setErr(7);
      toast.error("Please select a profile image.");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    if (empPic.raw) formData.append("image", empPic.raw);
    formData.append("idd", id);
    formData.append("designation", disgn.trim());
    formData.append("skills", skills.trim());
    formData.append("priority", priority);
    formData.append("name", name.trim());
    formData.append("description", desc.trim());
    formData.append("oldimage", oldImage);
    formData.append("status", status ? "1" : "0");

    Axios.post("/api/update_team", formData, {
      headers: { headKey: headKey },
    })
      .then((res) => {
        setSubmitting(false);
        if (res.data.status) {
          toast.success(res.data.msg || "Member updated");
          navigate("/dash/team");
        } else {
          toast.error(res.data.msg || "Something went wrong. Try again.");
        }
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Network error. Please try again.");
      });
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <Helmet>
        <title>Edit Team - Admin | Arawinz Soft Solutions</title>
      </Helmet>

      <AdminPageHeader
        eyebrow="Team"
        title="Edit team member"
        subtitle="Update name, designation, skills and visibility."
        backTo="/dash/team"
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Name"
            variant="outlined"
            error={err === 1}
            value={name}
            size="small"
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Designation"
            variant="outlined"
            error={err === 2}
            value={disgn}
            size="small"
            onChange={(e) => setDisgn(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Skills"
            variant="outlined"
            error={err === 3}
            value={skills}
            size="small"
            onChange={(e) => setSkills(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Priority"
            variant="outlined"
            error={err === 6}
            value={priority}
            size="small"
            onChange={(e) => setPriority(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Select...</MenuItem>
            {PRIORITY_OPTIONS.map((n) => (
              <MenuItem key={n} value={String(n)}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className={classes.statusRow}>
            <Switch
              checked={status}
              onChange={() => setStatus(!status)}
              color="primary"
            />
            <span>
              Status:{" "}
              <span className={status ? "on" : "off"}>
                {status ? "Visible" : "Hidden"}
              </span>
            </span>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Description"
            variant="outlined"
            error={err === 4}
            value={desc}
            size="small"
            multiline
            rows={4}
            onChange={(e) => setDesc(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Profile image</Typography>
          <Box className={classes.uploadWrap}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageChange}
              id="team-image-upload"
              type="file"
            />
            <label htmlFor="team-image-upload" style={{ display: "block" }}>
              <Box
                className={`${classes.uploadBox} ${
                  empPic.raw ? classes.uploadBoxFilled : ""
                }`}
              >
                <Box className={classes.uploadIconWrap}>
                  {empPic.raw ? (
                    <InsertPhotoOutlinedIcon />
                  ) : (
                    <CloudUploadOutlinedIcon />
                  )}
                </Box>
                <Box className={classes.uploadInfo}>
                  {empPic.raw ? (
                    <>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <CheckCircleOutlineIcon
                          style={{ fontSize: 16, color: "#10b981" }}
                        />
                        <strong>{empPic.raw.name}</strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        Click to choose a different file
                      </Typography>
                    </>
                  ) : (
                    <>
                      <span>
                        <strong>
                          {oldImage ? "Replace profile image" : "Upload profile image"}
                        </strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        JPEG, PNG or WebP, up to 5 MB
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </label>
            <Box style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {oldImage && !empPic.preview && (
                <Box>
                  <Typography
                    className={classes.uploadHint}
                    style={{ marginBottom: 4 }}
                  >
                    Current image
                  </Typography>
                  <img
                    src={"/static/team/" + oldImage}
                    alt="current"
                    className={classes.uploadPreview}
                    style={{ maxWidth: 180, maxHeight: 180 }}
                  />
                </Box>
              )}
              {empPic.preview && (
                <Box>
                  <Typography
                    className={classes.uploadHint}
                    style={{ marginBottom: 4 }}
                  >
                    New image preview
                  </Typography>
                  <img
                    src={empPic.preview}
                    alt="preview"
                    className={classes.uploadPreview}
                    style={{ maxWidth: 180, maxHeight: 180 }}
                  />
                </Box>
              )}
            </Box>
            {err === 7 && (
              <Typography style={{ color: "#dc2626", fontSize: "0.78rem" }}>
                Please select an image.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box className={classes.formFooter}>
        <Button
          className={classes.secondaryBtn}
          onClick={() => navigate("/dash/team")}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className={classes.primaryBtn}
          startIcon={!submitting && <SaveIcon />}
        >
          {submitting ? "Updating..." : "Save changes"}
        </Button>
      </Box>
    </div>
  );
};
